import React, { memo, useMemo, type PropsWithChildren, type ReactNode } from 'react';
import { isLoaderErrorAttributes } from '@jira/platform__react-loosely-lazy-error-handling/src/is-loader-error-attributes.tsx';
import {
	useJqlBuilderProps,
	useServerTimeUFO,
	type NestedEntryPoints,
} from '@atlassian/jira-spa-apps-issue-navigator-common/src/index.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import GlobalIssueNavigatorApp, {
	type Props as GlobalIssueNavigatorProps,
} from '@atlassian/jira-global-issue-navigator-nin/src/index.tsx';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { EnableNinChangeboardingProvider } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/enable-nin-changeboarding/index.tsx';
import type { View } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NINPageLoad } from '@atlassian/jira-issue-navigator/src/common/utils/performance-analytics.tsx';
import { SearchInputTypes } from '@atlassian/jira-issue-navigator/src/common/constants.tsx';
import { IssueNavigatorSubmitApdex } from '@atlassian/jira-spa-apps-issue-navigator-common/src/IssueNavigatorSubmitApdex.tsx';
import {
	getJqlFromParamValues,
	getSearchInput,
	useUpdateUrlQueryParamCallback,
} from '@atlassian/jira-issue-navigator/src/common/utils/spa-utils.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import {
	appBundleReadyMark,
	prefetchApiReadyMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { SmartQueryDetectedFlag } from '@atlassian/jira-issue-navigator-flags/src/ui/smart-query-detected-flag/index.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { getDefaultJqlGin as getDefaultJql } from '@atlassian/jira-router-resources-issue-navigator/src/controllers/get-data/utils.tsx';
import {
	issueNavigatorFilterRelayResource,
	issueNavigatorRelayResource,
	issueNavigatorHydrationRelayResource as issueNavigatorHydrationRelayResourceFromServer,
	issueNavigatorHydrationRelayResourceBrowserOnly,
} from '@atlassian/jira-router-resources-issue-navigator/src/index.tsx';
import { GlobalIssueNavigatorSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/global-app.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { IssueNavigatorFeatureProvider } from '@atlassian/jira-issue-navigator/src/controllers/features-context/index.tsx';

type CommonProps = Omit<
	GlobalIssueNavigatorProps,
	'defaultJql' | 'queryReference' | 'filterQueryReference' | 'searchInput'
>;

export type Props = CommonProps & {
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	jql: string | undefined;
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	initialised: boolean;
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	view?: View;
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	filter: string | undefined;
	preloadedEntryPoints?: NestedEntryPoints;
};

/**
 * Default search input value for type safety when `jira_nin_last_used_jql_tech_changes` is enabled.
 * Remove on FG cleanup.
 */
const deprecatedSearchInput = { jql: '', type: SearchInputTypes.JQL };

const MaybeReportErrors = componentWithFG(
	'improve_nin_slo_events',
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
	ReportErrors,
);

const AsyncGlobalIssueNavigatorWrapper = ({
	filter,
	jql: jqlParam,
	initialised,
	view,
	...props
}: Props) => {
	const getServerTimeUFO = useServerTimeUFO();

	const defaultJql = getDefaultJql();

	let searchInput;
	if (fg('jira_nin_last_used_jql_tech_changes')) {
		searchInput = deprecatedSearchInput;
	} else {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const onMutateJqlParam = useUpdateUrlQueryParamCallback('jql');
		const jql = getJqlFromParamValues(defaultJql, jqlParam, filter, undefined, onMutateJqlParam);

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		searchInput = useMemo(() => getSearchInput(jql, filter), [jql, filter]);
	}

	const filterForAnalytics = filter && isFilterId(filter) ? filter : 'default';

	const attributesNew = useMemo(
		() =>
			fg('jira_nin_last_used_jql_tech_changes')
				? {
						navigatorScope: 'global',
						...(fg('analytics_for_issue_key_included_in_nin_url') && {
							isIssueKeyPresent: !!props.issueKey,
						}),
					}
				: undefined,
		[props.issueKey],
	);

	const attributes = fg('jira_nin_last_used_jql_tech_changes')
		? attributesNew
		: {
				navigatorScope: 'global',
				viewLayout: view || '',
				// jql='' is a valid jql parameter, so we track empty jql
				jql: jqlParam !== undefined,
				filter: filterForAnalytics,
			};

	const extraInfo = {
		scope: 'global',
		filterId: filterForAnalytics,
		isJqlParamPresent: !!jqlParam,
		...(fg('analytics_for_issue_key_included_in_nin_url') && {
			isIssueKeyPresent: !!props.issueKey,
		}),
		...(initialised && getServerTimeUFO?.()),
		...attributes,
	};

	const response = useRelayResource(issueNavigatorRelayResource);
	const { queryReference } = response;

	const filterResponse = useRelayResource(issueNavigatorFilterRelayResource);
	const filterQueryReference = filterResponse.queryReference;

	const issueNavigatorHydrationRelayResource = fg('render_jql_builder_in_ssr')
		? issueNavigatorHydrationRelayResourceFromServer
		: issueNavigatorHydrationRelayResourceBrowserOnly;

	const hydrationResponse = useRelayResource(issueNavigatorHydrationRelayResource);
	const hydrationQueryReference = hydrationResponse.queryReference;

	const issueTableProps = useMemo(
		() => ({
			preloadedEntryPoints: {
				nativeIssueTableEntryPoint: props.preloadedEntryPoints?.nativeIssueTableEntryPoint,
			},
		}),
		[props.preloadedEntryPoints?.nativeIssueTableEntryPoint],
	);

	const jqlBuilderProps = useJqlBuilderProps(hydrationQueryReference, props.preloadedEntryPoints);

	// During SSR our issueNavigatorRelayResource must wait until the query finishes before returning the
	// queryReference as SSR does not support Suspense. We return the page skeleton as an intermediary state until
	// the query has resolved (or SSR times out and the query is loaded on the client).
	if (queryReference == null) {
		return <GlobalIssueNavigatorSkeleton />;
	}

	// During SSR our issueNavigatorFilterRelayResource must wait until the query finishes before returning the
	// queryReference as SSR does not support Suspense. We return the page skeleton as an intermediary state until
	// the query has resolved (or SSR times out and the query is loaded on the client).
	if (filterQueryReference == null) {
		return <GlobalIssueNavigatorSkeleton />;
	}

	// During SSR our issueNavigatorHydrationRelayResource must wait until the query finishes before returning the
	// queryReference as SSR does not support Suspense. We return the page skeleton as an intermediary state until
	// the query has resolved (or SSR times out and the query is loaded on the client).
	if (hydrationQueryReference == null && fg('render_jql_builder_in_ssr')) {
		return <GlobalIssueNavigatorSkeleton />;
	}

	return (
		<ContextualAnalyticsData
			attributes={attributes}
			{...(filter !== null &&
				filter !== undefined &&
				isFilterId(filter) &&
				!fg('jira_nin_last_used_jql_tech_changes') && {
					containers: { issueFilter: { id: filter } },
				})}
		>
			<MaybeReportErrors
				id="unhandled"
				packageName="jiraSpaAppsGlobalIssueNavigator"
				teamName="empanada"
				sendToPrivacyUnsafeSplunk
				attributes={isLoaderErrorAttributes}
			>
				{/* Render a Suspense boundary to handle the scenario where the resource query times out in SSR and
                content (i.e. nav) is still returned. Ideally we'd use the SPA level suspense boundary but there is some
                internal state logic in RLL that is preventing the fallback being rendered after the async component has
                successfully imported. */}
				<Placeholder name="global-issue-navigator-app" fallback={<GlobalIssueNavigatorSkeleton />}>
					<EnableNinChangeboardingProvider>
						<FeatureContext
							isConditionalFormattingEnabled={false}
							{...(fg('saved_list_view_technical_changes') && {
								isConditionalFormattingEditingEnabled: false,
							})}
							isMakeSpaceForGlobalRovoButtonEnabled={fg('global_rovo_button_in_jira')}
						>
							<GlobalIssueNavigatorApp
								{...props}
								searchInput={searchInput}
								defaultJql={defaultJql}
								queryReference={queryReference || undefined}
								filterQueryReference={filterQueryReference || undefined}
								jqlBuilderProps={
									fg('jql_builder_basic_ssr_stability_fixes')
										? jqlBuilderProps
										: { maybeHydrationQueryReference: hydrationQueryReference || undefined }
								}
								issueTableProps={issueTableProps}
								isMergedColumnSupportEnabled={fg('jira_list_merged_column_gate')}
								isFrozenColumnSupportEnabled={fg('issue_table_frozen_columns')}
								isInlineFieldConfigEnabled={false}
								isInBetweenColumnPickerEnabled={fg('jira_inline_field_config_gate')}
							/>
						</FeatureContext>
					</EnableNinChangeboardingProvider>
					{fg('jira_nin_last_used_jql_tech_changes') && (
						<>
							<IssueNavigatorSubmitApdex
								appName="global-issue-navigator"
								attributes={attributes}
								emitOnAnimationFrame
								scope="global"
							/>
							<JSErrorBoundary
								id="smartQueryDetectedFlag"
								packageName="jiraSpaAppsGlobalIssueNavigator"
								teamName="empanada"
								fallback="unmount"
							>
								<SmartQueryDetectedFlag />
							</JSErrorBoundary>
						</>
					)}
				</Placeholder>
			</MaybeReportErrors>

			{initialised && !fg('jira_nin_last_used_jql_tech_changes') && (
				<>
					{/* @ts-expect-error - Types for JFE BM3Metric and @atlassian/browser-metrics' PageLoadMetric are not compatible */}
					<SubmitApdex
						appName="global-issue-navigator"
						isExpectedToHaveSsr
						withMarks={[appBundleReadyMark, prefetchApiReadyMark]}
						ttrSlo={3000}
						ttiSlo={4000}
						extra={extraInfo}
						metric={NINPageLoad}
						emitOnAnimationFrame
					/>
					<SpaStatePageReady />
				</>
			)}
			<SubProductUpdater subProduct="platform" />
		</ContextualAnalyticsData>
	);
};

const NoOp = ({ children }: { children: ReactNode }) => children;
const FeatureContext = componentWithFG(
	'jira_list_conditional_formatting',
	IssueNavigatorFeatureProvider,
	NoOp,
);

export default memo<Props>(AsyncGlobalIssueNavigatorWrapper);
